import React from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';

const Backdrop = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const Modal = styled(motion.div)`
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 249, 250, 0.95) 100%);
  backdrop-filter: blur(15px);
  border-radius: 20px;
  padding: 40px;
  max-width: 600px;
  width: 100%;
  color: #2c3e50;
  box-shadow: 0 25px 80px rgba(0, 0, 0, 0.4);
  border: 2px solid rgba(255, 255, 255, 0.8);
  position: relative;
  max-height: 90vh;
  overflow-y: auto;

  @media (max-width: 768px) {
    padding: 30px;
    max-width: 90%;
  }

  @media (max-width: 480px) {
    padding: 25px;
    border-radius: 15px;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 15px;
  right: 15px;
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #7f8c8d;
  transition: color 0.3s ease;
  
  &:hover {
    color: #e74c3c;
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 30px;
`;

const Title = styled.h2`
  font-size: 2.2rem;
  margin-bottom: 20px;
  text-align: center;
  background: linear-gradient(45deg, #f39c12, #e67e22);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-weight: 800;
`;

const Subtitle = styled.p`
  color: #7f8c8d;
  font-size: 1.1rem;
  line-height: 1.5;
`;

const PricingContainer = styled.div`
  display: flex;
  gap: 20px;
  margin-bottom: 30px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 15px;
  }
`;

const PricingCard = styled.div`
  flex: 1;
  background: ${props => props.premium ? 
    'linear-gradient(135deg, #f39c12 0%, #e67e22 100%)' : 
    'rgba(255, 255, 255, 0.7)'
  };
  color: ${props => props.premium ? 'white' : '#2c3e50'};
  padding: 25px;
  border-radius: 15px;
  text-align: center;
  border: ${props => props.premium ? 
    '3px solid #f39c12' : 
    '2px solid #ecf0f1'
  };
  position: relative;
  
  ${props => props.premium && `
    transform: scale(1.05);
    box-shadow: 0 15px 40px rgba(243, 156, 18, 0.3);
  `}
`;

const PlanName = styled.h3`
  font-size: 1.4rem;
  margin-bottom: 10px;
  font-weight: 700;
`;

const Price = styled.div`
  font-size: 2rem;
  font-weight: 800;
  margin-bottom: 15px;
  
  span {
    font-size: 1rem;
    font-weight: 500;
  }
`;

const Badge = styled.div`
  position: absolute;
  top: -12px;
  left: 50%;
  transform: translateX(-50%);
  background: #e74c3c;
  color: white;
  padding: 6px 15px;
  border-radius: 15px;
  font-size: 0.8rem;
  font-weight: 600;
`;

const FeaturesList = styled.div`
  margin: 30px 0;
`;

const FeatureCategory = styled.div`
  margin-bottom: 25px;
`;

const CategoryTitle = styled.h4`
  font-size: 1.1rem;
  font-weight: 700;
  color: #34495e;
  margin-bottom: 15px;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const Feature = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px;
  background: ${props => props.premium ? 
    'rgba(78, 205, 196, 0.1)' : 
    'rgba(149, 165, 166, 0.1)'
  };
  border-radius: 8px;
  border-left: 4px solid ${props => props.premium ? '#4ecdc4' : '#95a5a6'};
`;

const FeatureIcon = styled.span`
  font-size: 1.2rem;
  color: ${props => props.premium ? '#4ecdc4' : '#95a5a6'};
`;

const FeatureText = styled.span`
  font-size: 0.9rem;
  color: #2c3e50;
  font-weight: 500;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 15px;
  margin-top: 30px;
  
  @media (max-width: 480px) {
    flex-direction: column;
  }
`;

const Button = styled(motion.button)`
  flex: 1;
  padding: 15px 25px;
  border: none;
  border-radius: 10px;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  
  ${props => props.primary ? `
    background: linear-gradient(45deg, #f39c12, #e67e22);
    color: white;
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 25px rgba(243, 156, 18, 0.3);
    }
  ` : `
    background: rgba(0, 0, 0, 0.05);
    color: #7f8c8d;
    border: 2px solid #ecf0f1;
    
    &:hover {
      background: rgba(0, 0, 0, 0.1);
    }
  `}
`;

const ComparisonNote = styled.div`
  background: rgba(52, 152, 219, 0.1);
  border-left: 4px solid #3498db;
  padding: 15px;
  margin-top: 20px;
  border-radius: 5px;
  font-size: 0.9rem;
  color: #2c3e50;
`;

const PremiumModal = ({ isOpen, onClose, feature = null }) => {
  const { user, isPremium } = useAuth();

  const features = {
    basic: [
      { icon: '🎯', text: 'Up to 6 wheel segments', premium: false },
      { icon: '👥', text: 'Unlimited players per room', premium: false },
      { icon: '🎨', text: 'Basic themes', premium: false },
      { icon: '📱', text: 'Mobile responsive', premium: false },
    ],
    premium: [
      { icon: '🎯', text: 'Unlimited wheel segments', premium: true },
      { icon: '🎨', text: 'Premium themes & customization', premium: true },
      { icon: '🔒', text: 'Private password-protected rooms', premium: true },
      { icon: '📊', text: 'Detailed game analytics', premium: true },
      { icon: '🏷️', text: 'Custom branding & logos', premium: true },
      { icon: '🎵', text: 'Custom sound effects', premium: true },
      { icon: '📈', text: 'Room history & statistics', premium: true },
      { icon: '🎭', text: 'Advanced player management', premium: true },
      { icon: '⚡', text: 'Priority support', premium: true },
      { icon: '🚫', text: 'Ad-free experience', premium: true },
    ]
  };

  const handleUpgrade = () => {
    if (!user) {
      alert('Please log in to upgrade to Premium');
      return;
    }
    
    // Navigate to payment page
    window.open('/payment', '_blank');
  };

  const getFeatureTitle = () => {
    if (feature === 'wheel_segments') return 'Unlimited Wheel Segments';
    if (feature === 'custom_themes') return 'Premium Themes';
    if (feature === 'private_rooms') return 'Private Rooms';
    if (feature === 'analytics') return 'Game Analytics';
    return 'Premium Features';
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <Backdrop
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <Modal
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          onClick={(e) => e.stopPropagation()}
        >
          <CloseButton onClick={onClose}>&times;</CloseButton>
          
          <Header>
            <Title>
              {isPremium ? '✨ You\'re Premium!' : `Upgrade for ${getFeatureTitle()}`}
            </Title>
            <Subtitle>
              {isPremium 
                ? 'Enjoy all premium features and make your party games legendary!'
                : 'Unlock premium features to create the ultimate party experience'
              }
            </Subtitle>
          </Header>

          {!isPremium && (
            <PricingContainer>
              <PricingCard>
                <PlanName>Free</PlanName>
                <Price>$0<span>/month</span></Price>
                <div>Perfect for casual parties</div>
              </PricingCard>
              
              <PricingCard premium>
                <Badge>Most Popular</Badge>
                <PlanName>Premium</PlanName>
                <Price>$4.99<span>/month</span></Price>
                <div>Unlock all features</div>
              </PricingCard>
            </PricingContainer>
          )}

          <FeaturesList>
            <FeatureCategory>
              <CategoryTitle>
                <span>🆓</span>
                Free Features
              </CategoryTitle>
              <FeatureGrid>
                {features.basic.map((feat, index) => (
                  <Feature key={index} premium={feat.premium}>
                    <FeatureIcon premium={feat.premium}>{feat.icon}</FeatureIcon>
                    <FeatureText>{feat.text}</FeatureText>
                  </Feature>
                ))}
              </FeatureGrid>
            </FeatureCategory>

            <FeatureCategory>
              <CategoryTitle>
                <span>✨</span>
                Premium Features
              </CategoryTitle>
              <FeatureGrid>
                {features.premium.map((feat, index) => (
                  <Feature key={index} premium={feat.premium}>
                    <FeatureIcon premium={feat.premium}>{feat.icon}</FeatureIcon>
                    <FeatureText>{feat.text}</FeatureText>
                  </Feature>
                ))}
              </FeatureGrid>
            </FeatureCategory>
          </FeaturesList>

          {!isPremium && (
            <ComparisonNote>
              💡 <strong>Pro tip:</strong> Premium users report 3x more engagement and fun at their parties! 
              Upgrade now and transform your drinking games forever.
            </ComparisonNote>
          )}

          <ActionButtons>
            {isPremium ? (
              <Button
                primary
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={onClose}
              >
                ✨ Continue with Premium
              </Button>
            ) : (
              <>
                <Button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={onClose}
                >
                  Maybe Later
                </Button>
                <Button
                  primary
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleUpgrade}
                >
                  🚀 Upgrade to Premium
                </Button>
              </>
            )}
          </ActionButtons>
        </Modal>
      </Backdrop>
    </AnimatePresence>
  );
};

export default PremiumModal; 